import { useEffect, useState } from "react";

import { ActivityTeaser } from "../components/ActivityTeaser";
import { ApprovalsHistory } from "../components/ApprovalsHistory";
import { Atividade } from "../components/Atividade";
import { CapabilitySummary } from "../components/CapabilitySummary";
import { ErrorPanel } from "../components/ErrorPanel";
import { InvoiceList } from "../components/InvoiceList";
import { KpiTiles } from "../components/KpiTiles";
import { LlmCostPanel } from "../components/LlmCostPanel";
import { NeedsYou } from "../components/NeedsYou";
import { RangePicker } from "../components/RangePicker";
import { SovereigntyPane } from "../components/SovereigntyPane";
import { StatusStrip, type AgentStatusState } from "../components/StatusStrip";
import { TabBar, type TabId } from "../components/TabBar";
import {
  ApiError,
  type ActivityRow,
  type CustomerView,
  type InvoiceTarget,
  fetchActivity,
  fetchCustomerView,
  fetchStatus,
  SessionExpiredError,
  TeamsAuthError,
} from "../lib/api";
import { t, type Language } from "../lib/i18n";
import {
  DEFAULT_RANGE_PRESET,
  presetLabelKey,
  presetToDays,
  type RangePreset,
} from "../lib/range";
import { RefreshProvider, useRefresh } from "../lib/refresh";
import type { TenantEntry } from "../lib/tenant";

interface DashboardProps {
  tenant: TenantEntry;
  language: Language;
  getAuthToken: () => Promise<string>;
  /** Live Teams channel name for the status-strip scope line. Optional so
   * the component renders standalone (unit tests) — null hides the line. */
  channelName?: string | null;
}

type ErrorState =
  | { kind: "none" }
  | { kind: "session_expired" }
  | { kind: "fetch_failed"; status: number | null; detail?: string };

// Auto-refresh on tab focus only when the data is at least this stale —
// avoids a refetch on every quick tab-in/tab-out. No background interval
// (won't hammer a backgrounded Teams tab).
const STALE_AFTER_MS = 60_000;

/** Top-level surface. Wraps the tab shell in a {@link RefreshProvider} so
 * the status strip's ⟳ + the focus auto-refresh drive every panel from
 * one shared signal. */
export function Dashboard(props: DashboardProps): JSX.Element {
  return (
    <RefreshProvider>
      <DashboardInner {...props} />
    </RefreshProvider>
  );
}

function DashboardInner({
  tenant,
  language,
  getAuthToken,
  channelName = null,
}: DashboardProps): JSX.Element {
  const { reloadKey, lastRefreshedAt, triggerReload } = useRefresh();
  const [view, setView] = useState<CustomerView | null>(null);
  const [feed, setFeed] = useState<ActivityRow[] | null>(null);
  const [error, setError] = useState<ErrorState>({ kind: "none" });
  const [statusState, setStatusState] = useState<AgentStatusState>({
    kind: "loading",
  });
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  // Drill/filter target threaded from Overview (Needs-you CTA → correlationId
  // drill-in; KPI tile → status filter) into the Invoices tab's list. `seq`
  // bumps even when the same target is chosen twice so the list re-applies it.
  const [invoiceNav, setInvoiceNav] = useState<{
    target: InvoiceTarget;
    seq: number;
  } | null>(null);
  const openInvoices = (target: InvoiceTarget): void => {
    setInvoiceNav((prev) => ({ target, seq: (prev?.seq ?? 0) + 1 }));
    setActiveTab("invoices");
  };
  const [rangePreset, setRangePreset] = useState<RangePreset>(
    DEFAULT_RANGE_PRESET,
  );
  // Rolling window length (days) for the KPI/cost/sovereignty reads. A
  // primitive, so it's a stable useEffect dependency; recomputed per render
  // is cheap and keeps "today"/"this year" tracking the clock.
  const periodDays = presetToDays(rangePreset);

  // Primary fetch (KPIs + activity feed) — keyed on the shared reloadKey
  // so the ⟳ button + focus auto-refresh re-pull it alongside the panels.
  useEffect(() => {
    const deps = { backendUrl: tenant.backendUrl, getAuthToken };
    let cancelled = false;

    setError({ kind: "none" });
    setView(null);
    setFeed(null);

    // The teaser is count-based (last-5, both outcomes, ignores the range);
    // it shares this load so the Overview shows one unified loading state.
    Promise.all([
      fetchCustomerView(deps, periodDays),
      fetchActivity(deps, { limit: 5 }),
    ])
      .then(([nextView, nextPage]) => {
        if (cancelled) return;
        setView(nextView);
        setFeed(nextPage.items);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof SessionExpiredError) {
          setError({ kind: "session_expired" });
          return;
        }
        const status = err instanceof ApiError ? err.status : null;
        const detail =
          err instanceof TeamsAuthError
            ? err.reason
            : status === null && err instanceof Error
              ? err.message
              : undefined;
        setError({ kind: "fetch_failed", status, detail });
      });

    return () => {
      cancelled = true;
    };
  }, [tenant.backendUrl, getAuthToken, reloadKey, periodDays]);

  // Status fetch — owned here so the tiny liveness read runs once per
  // refresh and is shared by StatusStrip + VersionDetail. Independent of
  // the primary fetch: a /status hiccup degrades the strip to neutral
  // without blanking the whole page (the strip handles its own error).
  useEffect(() => {
    const deps = { backendUrl: tenant.backendUrl, getAuthToken };
    let cancelled = false;
    setStatusState({ kind: "loading" });
    fetchStatus(deps)
      .then((status) => {
        if (!cancelled) setStatusState({ kind: "loaded", status });
      })
      .catch(() => {
        if (!cancelled) setStatusState({ kind: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [tenant.backendUrl, getAuthToken, reloadKey]);

  // Auto-refresh when the tab regains focus AND the data is stale.
  useEffect(() => {
    const maybeRefresh = (): void => {
      if (document.visibilityState !== "visible") return;
      if (
        lastRefreshedAt === null ||
        Date.now() - lastRefreshedAt > STALE_AFTER_MS
      ) {
        triggerReload();
      }
    };
    window.addEventListener("focus", maybeRefresh);
    document.addEventListener("visibilitychange", maybeRefresh);
    return () => {
      window.removeEventListener("focus", maybeRefresh);
      document.removeEventListener("visibilitychange", maybeRefresh);
    };
  }, [lastRefreshedAt, triggerReload]);

  const deps = { backendUrl: tenant.backendUrl, getAuthToken };

  // A hard auth/fetch failure on the PRIMARY load takes over the surface —
  // it must not hide behind a tab (per the F37 UX-redesign plan).
  if (error.kind === "session_expired") {
    return (
      <ErrorPanel
        titleKey="error.session_expired.title"
        bodyKey="error.session_expired.body"
        retryLabelKey="error.session_expired.action"
        language={language}
        onRetry={triggerReload}
      />
    );
  }

  if (error.kind === "fetch_failed") {
    return (
      <ErrorPanel
        titleKey="error.fetch_failed.title"
        bodyKey="error.fetch_failed.body"
        language={language}
        onRetry={triggerReload}
        detail={error.detail}
      />
    );
  }

  // All tab bodies stay mounted; visibility toggles via mutually-exclusive
  // `flex`/`hidden` CLASSES. We must NOT pair the `hidden` HTML attribute
  // with a `flex` display utility: Tailwind's author-stylesheet
  // `.flex{display:flex}` beats the UA `[hidden]{display:none}` rule (author
  // origin wins over UA at equal specificity), so the panel would stay
  // visible and tab-switching would be a visual no-op. Toggling between the
  // `flex` and `hidden` utility classes keeps the display rule unambiguous.
  // Switching tabs does NOT re-fire each panel's self-fetch (bodies stay
  // mounted) — the load-bearing choice from the redesign plan.
  const tabPanel = (id: TabId, body: JSX.Element): JSX.Element => {
    const active = activeTab === id;
    return (
      <div
        role="tabpanel"
        id={`tabpanel-${id}`}
        aria-labelledby={`tab-${id}`}
        aria-hidden={!active}
        className={`mt-4 flex-col gap-6 ${active ? "flex" : "hidden"}`}
      >
        {body}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-xl font-semibold text-kwanza-ink">
          {t("app.title", language)}
        </h1>
        {/* The range label used to sit here, next to the product subtitle,
         * as if it governed everything below it — while Needs-you and the
         * activity teaser deliberately ignore it (design review finding 7).
         * It now lives on the sections it actually scopes. */}
        <p className="text-sm text-kwanza-inkSoft">{t("app.subtitle", language)}</p>
      </header>

      <StatusStrip
        statusState={statusState}
        language={language}
        channelName={channelName}
      />
      <RangePicker
        selected={rangePreset}
        onSelect={setRangePreset}
        language={language}
      />
      <TabBar active={activeTab} onSelect={setActiveTab} language={language} />

      {/* Overview panel order (§12.6, Task 8): NeedsYou → KpiTiles (3 +
       * secondary line) → CapabilitySummary → ActivityTeaser. The old
       * summary sentence is gone — the KPI tiles + their muted secondary
       * line (Task 4) carry that figure now.
       *
       * Above 1024px the same four cards take two columns (design review
       * finding 9 + mockup 2D-2): one 1152px column meant ~500px of a
       * desktop channel tab did nothing while "what just happened" sat
       * below the fold. Needs-you stays full-bleed across the top — it must
       * never compete for the eye — and below `lg` the grid collapses to
       * today's exact single column. One wrapper, no new components. */}
      {tabPanel(
        "overview",
        <>
          <NeedsYou
            deps={deps}
            language={language}
            reloadKey={reloadKey}
            onOpen={openInvoices}
          />
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:items-start">
            <div className="flex flex-col gap-6 lg:col-span-2">
              <KpiTiles
                view={view}
                language={language}
                periodLabel={t(presetLabelKey(rangePreset), language)}
                onTile={openInvoices}
              />
              <CapabilitySummary
                deps={deps}
                language={language}
                periodDays={periodDays}
                reloadKey={reloadKey}
                onOpenTrust={() => setActiveTab("trust")}
              />
            </div>
            <ActivityTeaser
              rows={feed}
              language={language}
              onSeeAll={() => setActiveTab("atividade")}
            />
          </div>
        </>,
      )}

      {tabPanel(
        "invoices",
        <>
          <InvoiceList
            deps={deps}
            language={language}
            periodDays={periodDays}
            reloadKey={reloadKey}
            nav={invoiceNav}
          />
          <ApprovalsHistory deps={deps} language={language} reloadKey={reloadKey} />
        </>,
      )}

      {tabPanel(
        "atividade",
        <Atividade
          deps={deps}
          language={language}
          rangePreset={rangePreset}
          reloadKey={reloadKey}
        />,
      )}

      {tabPanel(
        "trust",
        <>
          <SovereigntyPane
            deps={deps}
            language={language}
            reloadKey={reloadKey}
            periodDays={periodDays}
            rangeLabel={t(presetLabelKey(rangePreset), language)}
          />
          <LlmCostPanel
            deps={deps}
            language={language}
            reloadKey={reloadKey}
            periodDays={periodDays}
          />
        </>,
      )}
    </div>
  );
}
